import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useNavigation } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import { spacing, typography } from '../../theme';
import { useColors, ThemeColors } from '../../hooks/useColors';
import type { Vendor } from '../../types/supabase';
import { getWebImageUrl } from '../../utils/format';

interface VendorCardProps {
  vendor: Vendor;
}

export function VendorCard({ vendor }: VendorCardProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation();

  // Transform image URLs for web compatibility (HEIC -> JPEG)
  const bannerUrl = Platform.OS === 'web' ? getWebImageUrl(vendor.banner_image) : vendor.banner_image;
  const logoUrl = Platform.OS === 'web' ? getWebImageUrl(vendor.logo_image) : vendor.logo_image;

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={() => navigation.navigate('Vendor', { id: vendor.id })}
    >
      {bannerUrl ? (
        <Image
          source={{ uri: bannerUrl }}
          style={styles.banner}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={0}
        />
      ) : (
        <View style={[styles.banner, styles.bannerPlaceholder]}>
          <Feather name="image" size={24} color={colors.textLight} />
        </View>
      )}

      <View style={styles.content}>
        {logoUrl ? (
          <Image
            source={{ uri: logoUrl }}
            style={styles.logo}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
          />
        ) : (
          <View style={[styles.logo, styles.logoPlaceholder]}>
            <Feather name="shopping-bag" size={20} color={colors.textLight} />
          </View>
        )}

        <View style={styles.info}>
          <Text style={styles.name}>{vendor.store_name}</Text>
          <Text style={styles.description} numberOfLines={2}>
            {vendor.description}
          </Text>
          <View style={styles.metadata}>
            <View style={styles.metadataItem}>
              <Feather name="map-pin" size={14} color={colors.textLight} />
              <Text style={styles.metadataText}>{vendor.borough}</Text>
            </View>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    backgroundColor: colors.background,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  banner: {
    width: '100%',
    height: 120,
    backgroundColor: colors.backgroundSecondary,
  },
  bannerPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: spacing.md,
    flexDirection: 'row',
    gap: spacing.md,
  },
  logo: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginTop: -30,
    borderWidth: 3,
    borderColor: colors.background,
    backgroundColor: colors.backgroundSecondary,
  },
  logoPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
  },
  name: {
    ...typography.subtitle,
    marginBottom: spacing.xs,
  },
  description: {
    ...typography.body,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  metadata: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metadataText: {
    ...typography.caption,
    color: colors.textLight,
  },
}); 